import React, { useRef, useCallback } from 'react';
import noop from 'lodash/noop';
import FinalForm, { Field } from '@framework/ui/Organisms/FinalForm';
import FinsightModal from '@/creditflowresearch/ui/common/components/FinsightModal';
import EmailInput from '@/creditflowresearch/ui/common/components/EmailInput';
import Textarea from '@creditflowresearch/uilib/Textarea';
import UploadFile from '@/creditflowresearch/ui/common/components/UploadFile';
import ReCaptcha from '@framework/ui/Molecules/Form/ReCaptcha';
import Button from '@creditflowresearch/uilib/Button';
import validate from '@/creditflowresearch/ui/common/components/FinsightModal/modalValidator';
import { CORRECTION_MODAL_FORM, SUBMIT_CORRECTION_MODAL_FORM } from './constants';
import { TFinalFormRenderProp } from '@framework/ui/Organisms/FinalForm/interfaces';

import spacesStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';
import styles from '@/creditflowresearch/ui/components/deals/CorrectionModal/correctionModal.module.scss';

interface ICorrectionDeal {
  id: string,
  name: string,
}

interface ICorrectionModalFormProps {
  deal: ICorrectionDeal,
  isVisible: boolean,
  isSubmitSucceed: boolean,
  isFetching: boolean,
  hideModal?: () => void,
  submitForm?: (id: string, inputUploadFile: any, payload: {}) => void,
  resetModal?: () => void,
}

const CorrectionModal = (
  {
    deal,
    submitForm,
    resetModal,
    hideModal,
    isVisible,
    isFetching,
    isSubmitSucceed,
  }: ICorrectionModalFormProps,
) => {
  let resetFinalFormState = noop;
  const inputUploadFile = useRef(null);

  const modalTitle = 'Thank You for Submitting a Correction';
  const modalDescription = `Please provide comments or details on the correction(s) below.
    For faster turn around and verification purposes, please provide any supporting documents (e.g. Final OMs, syndicate email, URL).
    Attach multiple files via ZIP if necessary.`;

  const onCloseClicked = useCallback(
    () => {
      hideModal();
      inputUploadFile.current.deleteFile();
      resetFinalFormState();
      resetModal();
    },
    [
      resetFinalFormState,
      resetModal,
      hideModal,
    ],
  );

  const renderFields = useCallback<TFinalFormRenderProp>(
    ({
      valid,
      form,
    }) => {
      resetFinalFormState = form.restart;
      return (
        <>
          <div className={ styles.headerText }>
            <h1>{ modalTitle }</h1>
            <p className={ styles.modalDescription }>
              { modalDescription }
            </p>
            {
              deal?.name && (
                <p className={ styles.dealName }>Deal: { deal.name }</p>
              )
            }
          </div>
          <Field
            name="email"
            type="email"
            className={ styles.inputContainer }
            component={ EmailInput }
            dataTest="correctionFormEmail"
          />
          <Field
            textareaClassName={ styles.textarea }
            placeholder="Please provide comments here"
            name="comments"
            component={ Textarea }
            dataTest="correctionFormTextArea"
          />
          <UploadFile
            formName={ `CORRECTION_MODAL_FORM-${ deal.id }` }
            ref={ inputUploadFile }
          />
          { /* We need the check below because ReCaptcha is suspected in flooding sentry with errors, see FIN-10487 */ }
          { isVisible && (
            <div className={ spacesStyles.mbxl }>
              <Field
                name="recaptcha"
                component={ ReCaptcha }
                dataTest="recaptchaCorrectionModal"
              />
            </div>
          ) }
          <Button
            dataTest={ SUBMIT_CORRECTION_MODAL_FORM }
            type="submit"
            disabled={ !valid }
            variant="solid"
            color="primary"
          >
            Submit
          </Button>
          <Button
            variant="text"
            color="primary"
            onClick={ onCloseClicked }
            dataTest="correctionModalCloseButton"
          >
            Cancel
          </Button>
        </>
      );
    },
    [isVisible, resetFinalFormState, onCloseClicked],
  );

  const onSumbit = useCallback(
    (payload) => submitForm(deal?.id, inputUploadFile.current.getFile(), payload),
    [submitForm, deal],
  );

  return (
    <FinsightModal
      hideOnOverlayClicked
      successText="Your correction has been submitted for review."
      modalTitle={ <>Instant Market<br />Color</> }
      onCloseClicked={ onCloseClicked }
      isVisible={ isVisible }
      dataTest="correctionModal"
      isFetching={ isFetching }
      isSubmitSucceed={ isSubmitSucceed }
    >
      <FinalForm
        dataTest={ CORRECTION_MODAL_FORM }
        render={ renderFields }
        onSubmit={ onSumbit }
        validate={ validate }
      />
    </FinsightModal>
  );
};

export default (CorrectionModal);
